import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../auth";
import { errMsg } from "../api";
import { Wordmark, Emblem } from "../components/Brand";
import AccountMenu from "../components/AccountMenu";
import { InstallBanner } from "../components/InstallPrompt";

function LoginCard() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(""); setBusy(true);
    try { await login(u, p); nav("/"); }
    catch (e) { setErr(errMsg(e, "Could not sign in")); }
    finally { setBusy(false); }
  };
  const onKey = (e) => { if (e.key === "Enter") submit(); };

  return (
    <div className="llh-card llh-card--hero llh-login-card">
      <h2 className="llh-card-title llh-mb-18">
        Sign in
      </h2>
      <label className="llh-label">Username</label>
      <input className="llh-input llh-mb-14" value={u} autoCapitalize="none" autoCorrect="off"
        onChange={(e) => setU(e.target.value)} onKeyDown={onKey} />
      <label className="llh-label">Password</label>
      <input className="llh-input llh-mb-14" type="password" value={p}
        onChange={(e) => setP(e.target.value)} onKeyDown={onKey} />
      {err && <div className="llh-error-text llh-mb-12">{err}</div>}
      <button className="llh-btn-cta llh-btn--dim6" disabled={busy} onClick={submit}>
        {busy ? "Signing in..." : "Sign in"}
      </button>
      <p className="llh-help-text llh-mb-0">
        Accounts are issued by your coach. If you need access or a password reset, ask in class.
      </p>
    </div>
  );
}

function ToolTile({ to, title, blurb, available }) {
  const body = (
    <div className={available ? "llh-card llh-card--hero llh-tile" : "llh-card llh-card--hero llh-tile llh-o-55"}>
      <div className="llh-tile-head">
        <h3 className="llh-tile-title">{title}</h3>
        {!available && <span className="llh-badge">Soon</span>}
      </div>
      <p className="llh-tile-blurb">{blurb}</p>
      {available && <div className="llh-tile-open">Open →</div>}
    </div>
  );
  return available ? <Link to={to} className="llh-block-link">{body}</Link> : <div>{body}</div>;
}

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="llh-landing">
      <header className="llh-header-hero">
        <Wordmark />
        {user && <AccountMenu showTools={false} />}
      </header>

      <main className="llh-hero-main">
        {/* Hero */}
        <section className={user ? "llh-hero llh-hero--in" : "llh-hero"}>
          <div className="llh-hero-emblem">
            <Emblem size={user ? 60 : 84} />
          </div>
          <h1 className={user ? "llh-hero-title llh-hero-title--in" : "llh-hero-title"}>
            {user ? `Welcome back, ${user.display_name || user.username}` : "Strength for the Longsword"}
          </h1>
          {!user && (
            <p className="llh-hero-sub">
              The same steel that arms the sword builds the body that wields it. Track your
              barbell work for the Leichtenauer Lift'n Hour and watch your numbers climb.
            </p>
          )}
        </section>

        {user ? (
          <section>
            <h2 className="llh-section-head">
              Choose your tool
            </h2>
            <div className="llh-tool-grid">
              <ToolTile to="/app" title="Ironlifts 5×5" available
                blurb="Log your 5×5 barbell sessions, get warmups and plate math, and chart your progress on every lift." />
              <ToolTile title="Macebell Flow" available={false}
                blurb="Mace and gada flow tracking. In development, arriving once the program is dialed in." />
              {user.role === "admin" && (
                <ToolTile to="/admin/overview" title="Coaching" available
                  blurb="See who's training at a glance and review sessions, with coach feedback on each workout." />
              )}
              {user.role === "admin" && (
                <ToolTile to="/admin" title="Admin Panel" available
                  blurb="Create and manage student accounts, set display names, reset passwords, and change roles." />
              )}
            </div>
          </section>
        ) : (
          <section className="llh-split-grid">
            <div>
              <LoginCard />
            </div>
            <div className="llh-card llh-card--hero">
              <h2 className="llh-card-title llh-mb-14">
                About the hour
              </h2>
              <p className="llh-prose llh-mt-0">
                The Leichtenauer Lift'n Hour is the strength block that backs our longsword
                practice. Stronger legs, back, and shoulders mean better structure in the bind
                and more durable joints under load.
              </p>
              <p className="llh-prose">
                Ironlifts 5×5 runs the classic linear program: two alternating sessions, five
                sets of five, small jumps each time you train. The app builds your warmup,
                tells you exactly which plates to load, and keeps the full history of every lift
                so progress is never guesswork.
              </p>
              <p className="llh-prose llh-mb-0">
                Sign in with the account your coach gave you to begin. Your data is saved to your
                account, so it follows you from the rack to your phone to home.
              </p>
            </div>
          </section>
        )}

        <InstallBanner variant="inline" />
      </main>

      <footer className="llh-footer">
        <a href="https://sachema.com/schedule" target="_blank" rel="noreferrer" className="llh-footer-link">Leichtenauer Lift'n Hour</a>
        {" · "}
        <a href="https://www.sachema.com" target="_blank" rel="noreferrer" className="llh-footer-link">Sacramento Historical Fencing Academy</a>
      </footer>
    </div>
  );
}
